/**
 * How a typed query is matched against a LABEL a person reads — a specialty, a
 * profession, a country, a practice. One owner, because five screens were each
 * carrying their own `label.toLowerCase().includes(query)` and therefore each
 * carrying the same two defects: "pediatrie" missed "Pédiatrie", and "MPR" —
 * what a physiotherapist actually types — missed "Médecine physique et de
 * réadaptation" entirely.
 *
 * Deliberately NOT fuzzy. Fuzziness (Fuse.js, used by the Discovery filter
 * panels) buys edit distance, which is the wrong currency here: the queries that
 * fail on these screens are acronyms and trade abbreviations, which are far from
 * their label by edit distance and exact by construction. Edit distance is bought
 * from the model instead (`useLabelSearchAssist`), where it costs nothing to be
 * right about.
 */

import Fuse from "fuse.js";

/** Words that carry no initial. Eight languages, so the list is the union — a
 * false member only makes one acronym slightly more permissive, never less. */
const STOP_WORDS = new Set([
  "a",
  "al",
  "and",
  "an",
  "as",
  "at",
  "auf",
  "aux",
  "con",
  "da",
  "das",
  "de",
  "degli",
  "dei",
  "del",
  "della",
  "delle",
  "der",
  "des",
  "di",
  "die",
  "do",
  "dos",
  "du",
  "e",
  "el",
  "en",
  "et",
  "för",
  "i",
  "il",
  "in",
  "la",
  "las",
  "le",
  "les",
  "lo",
  "los",
  "of",
  "och",
  "on",
  "or",
  "para",
  "per",
  "por",
  "the",
  "to",
  "und",
  "y",
  "zu",
  "zur",
]);

/**
 * Ligatures NFD does not decompose. Nobody reaches "œ" on a phone keyboard, and
 * the catalogue is full of it ("Chirurgie des cancers de l'œsophage",
 * "Rééducation en oncologie et lymphœdème") — so without this, two of the words
 * a French practitioner is most likely to type are unsearchable.
 */
const LIGATURES: ReadonlyArray<readonly [RegExp, string]> = [
  [/œ/g, "oe"],
  [/æ/g, "ae"],
  [/ß/g, "ss"],
];

/**
 * Lowercase + strip diacritics, so "Pediatr" matches "Pédiatrie" and "Cote"
 * matches "Côte d'Ivoire".
 */
export function normalizeForLabelSearch(value: string): string {
  const lowered = value.trim().toLowerCase();
  const unligatured = LIGATURES.reduce(
    (text, [pattern, replacement]) => text.replace(pattern, replacement),
    lowered
  );
  return unligatured.normalize("NFD").replace(/\p{Diacritic}/gu, "");
}

/**
 * The words of a label, already normalized. Split on whitespace, hyphens,
 * slashes, apostrophes and parentheses — "Oto-rhino-laryngologie" is three words
 * for an acronym even though it is one for a space-split, and that single case
 * is most of what makes acronym matching work in French.
 */
function labelWords(label: string): string[] {
  return normalizeForLabelSearch(label)
    .split(/[^\p{L}\p{N}]+/u)
    .filter((word) => word.length > 0);
}

/**
 * The acronym a label would be abbreviated to: the initials of its significant
 * words. "Médecine physique et de réadaptation" → "mpr".
 */
function labelAcronym(words: readonly string[]): string {
  return words
    .filter((word) => !STOP_WORDS.has(word))
    .map((word) => word.slice(0, 1))
    .join("");
}

/**
 * Is `query` a run of characters that STARTS a word of `label`?
 *
 * Not a plain `includes`: "ENT" is inside "complementary", "dentistry" and
 * "gastroenterologie", so a substring match answers a three-letter acronym with
 * nine wrong rows and buries the right one. Nobody searches from the middle of
 * a word; requiring the boundary costs nothing and removes the whole class.
 */
function startsAWord(label: string, query: string): boolean {
  let from = 0;
  for (;;) {
    const at = label.indexOf(query, from);
    if (at === -1) return false;
    if (at === 0) return true;
    // `slice`, not `label[at - 1]`: indexing is `string | undefined` under
    // `noUncheckedIndexedAccess`, and `at >= 1` here makes the guard pure noise.
    if (!/[\p{L}\p{N}]/u.test(label.slice(at - 1, at))) return true;
    from = at + 1;
  }
}

/**
 * Does `label` answer `query`?
 *
 * Three ways, in falling order of confidence: the query is a substring of the
 * label; the query IS the label's acronym; every word of the query prefixes a
 * distinct word of the label ("med phys readapt", and equally "chirurgie
 * thoracique" against "Chirurgie mini-invasive thoracique", which a plain
 * substring misses).
 *
 * An empty query matches nothing — callers keep the unfiltered list themselves,
 * which is a different thing from "every row matches".
 */
function matchesLabel(args: {
  readonly label: string;
  readonly query: string;
}): boolean {
  const query = normalizeForLabelSearch(args.query);
  if (query === "") return false;

  const label = normalizeForLabelSearch(args.label);
  if (startsAWord(label, query)) return true;

  const words = labelWords(args.label);
  // Punctuation dropped so "M.P.R." reaches the acronym branch — the substring
  // branch above has already answered every unpunctuated one-letter query, so
  // no extra floor is needed here.
  const queryLetters = query.replace(/[^\p{L}\p{N}]/gu, "");
  if (queryLetters.length > 0 && labelAcronym(words) === queryLetters)
    return true;

  const queryWords = query.split(/[^\p{L}\p{N}]+/u).filter((w) => w.length > 0);
  if (queryWords.length < 2) return false;
  // Each query word consumes ONE label word, so "chirurgie chirurgie" does not
  // match a label holding the word once.
  const unused = [...words];
  return queryWords.every((queryWord) => {
    const index = unused.findIndex((word) => word.startsWith(queryWord));
    if (index === -1) return false;
    unused.splice(index, 1);
    return true;
  });
}

/**
 * Fuse buys edit distance, and edit distance is meaningless on a short query: at
 * three characters every entry of the catalogue is one edit from every other, so
 * "ENT" came back with eleven rows. A typo on three letters is not recoverable
 * anyway.
 */
const FUSE_MIN_QUERY_LENGTH = 4;

/**
 * The rows of `items` that answer `query`, best first, or all of them when the
 * query is empty.
 *
 * Two layers, and the order between them is the point. `matchesLabel` first, in
 * the input order the caller chose, because those rows are right by construction.
 * Then Fuse appends what it recognizes through a typo — "cardiolgie" reaches
 * "Cardiologie" — ranked by its own score. Measured: the exact layer alone is at
 * 0 % on typos, and typos are what the practitioner stares at for the two
 * seconds the model layer takes to answer.
 *
 * Same Fuse config as the Discovery filter panels, at the threshold the
 * benchmark kept: 0.4 buys seven points of recall and pays for them with five
 * times the rows and two catalogue traps answered out of ten.
 */
export function searchLabels<T>(args: {
  readonly items: readonly T[];
  readonly query: string;
  readonly labelOf: (item: T) => string;
}): readonly T[] {
  const query = args.query.trim();
  if (query === "") return args.items;

  const exact = args.items.filter((item) =>
    matchesLabel({ label: args.labelOf(item), query })
  );
  if (query.length < FUSE_MIN_QUERY_LENGTH) return exact;

  const taken = new Set(exact);
  const rest = args.items
    .filter((item) => !taken.has(item))
    .map((item) => ({ item, label: args.labelOf(item) }));
  const fuse = new Fuse(rest, {
    includeScore: true,
    threshold: 0.25,
    ignoreDiacritics: true,
    keys: ["label"],
  });
  return [...exact, ...fuse.search(query).map((result) => result.item.item)];
}
